import React, { useState, useEffect } from "react";
import { Button, Flex, Input, message, Select } from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  UpOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const FieldActionConfigBuilder: React.FC = () => {
  interface Action {
    actionID: string;
    execType: string;
    execData: string;
  }

  interface ButtonConfig {
    id: string; // internal id only
    label: string;
    icon: string;
    className: string;
    actionType: string;
    actions: Action[];
  }

  const generateId = () =>
    Date.now().toString() + Math.random().toString(36).substring(2, 6); // NOSONAR : igonre

  const [buttons, setButtons] = useState<ButtonConfig[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [jsonInput, setJsonInput] = useState("");

  const selected = buttons.find((btn) => btn.id === selectedId) || null;

  // Load JSON (accepts {} or [])
  const loadJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);

      let rawButtons: any[] = [];
      if (Array.isArray(parsed)) {
        rawButtons = parsed;
      } else if (typeof parsed === "object" && parsed !== null) {
        rawButtons = [parsed]; // wrap into array
      } else {
        throw new Error("Expected object or array");
      }

      const withIds = rawButtons.map((btn: Partial<ButtonConfig>) => ({
        id: generateId(),
        label: btn.label ?? "",
        icon: btn.icon ?? "",
        className: btn.className ?? "",
        actionType: btn.actionType ?? "",
        actions: (btn.actions ?? []).map((act: Partial<Action>) => ({
          actionID: act.actionID ?? "",
          execType: act.execType ?? "",
          execData: act.execData ?? "",
        })),
      }));

      setButtons(withIds);
      setSelectedId(withIds.length === 1 ? withIds[0].id : null);
    } catch (err: any) {
      message.error("Invalid JSON format: " + err.message);
    }
  };

  // Update Query JSON
  const updateQueryJsonInput = () => {
    try {
      const parseddata = JSON.parse(jsonInput);
      const stringified = JSON.stringify(parseddata);
      const query = `Update FieldInterfaceMapping SET FieldActionConfig = '${stringified}' Where Id = ??`;
      navigator.clipboard
        .writeText(query)
        .then(() => {
          message.success("JSON Query copied!");
        })
        .catch(() => {
          message.error("Failed to copy query!");
        });
    } catch {
      message.error("Invalid JSON format");
    }
  };

  const updateSelected = (updated: ButtonConfig) => {
    setButtons((btns) =>
      btns.map((btn) => (btn.id === updated.id ? updated : btn))
    );
  };

  const addButton = () => {
    const newBtn: ButtonConfig = {
      id: generateId(),
      label: "",
      icon: "",
      className: "",
      actionType: "",
      actions: [],
    };
    setButtons([...buttons, newBtn]);
    setSelectedId(newBtn.id);
  };

  const deleteButton = (id: string) => {
    setButtons((btns) => btns.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // validate JSON
  const validateJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      message.success("Valid JSON format!");
      return JSON.stringify(parsed, null, 2);
    } catch {
      message.error("Invalid JSON format");
      return json;
    }
  };

  const beautifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed, null, 2);
    } catch {
      message.error("Invalid JSON format for beautify");
      return json;
    }
  };

  const minifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed);
    } catch {
      message.error("Invalid JSON format for minify");
      return json;
    }
  };

  // Sync `jsonInput`
  useEffect(() => {
    if (buttons.length === 0) {
      setJsonInput("");
    } else if (selected) {
      // ✅ If button selected, show object {}
      const { id, ...rest } = selected;
      setJsonInput(JSON.stringify(rest, null, 2));
    } else {
      // Otherwise, fallback to array
      const cleaned = buttons.map(({ id, ...rest }) => rest);
      setJsonInput(JSON.stringify(cleaned, null, 2));
    }
  }, [buttons, selected]);

  const handleExecTypeChange = (execType: string, index: number) => {
    if (selected) {
      const updatedActions = [...selected.actions];
      updatedActions[index] = { ...updatedActions[index], execType };
      updateSelected({ ...selected, actions: updatedActions });
    }
  };

  const moveButtonUp = (id: string) => {
    const index = buttons.findIndex((btns) => btns.id === id);
    if (index > 0) {
      const newButtons = [...buttons];
      const [movedButton] = newButtons.splice(index, 1);
      newButtons.splice(index - 1, 0, movedButton);
      setButtons(newButtons);
    }
  };

  const moveButtonDown = (id: string) => {
    const index = buttons.findIndex((btns) => btns.id === id);
    if (index < buttons.length - 1) {
      const newButtons = [...buttons];
      const [movedButton] = newButtons.splice(index, 1);
      newButtons.splice(index + 1, 0, movedButton);
      setButtons(newButtons);
    }
  };

  const moveActionUp = (i: number) => {
    if (selected && i > 0) {
      const updated = [...selected.actions];
      const [moved] = updated.splice(i, 1);
      updated.splice(i - 1, 0, moved);
      updateSelected({ ...selected, actions: updated });
    }
  };

  const moveActionDown = (i: number) => {
    if (selected && i < selected.actions.length - 1) {
      const updated = [...selected.actions];
      const [moved] = updated.splice(i, 1);
      updated.splice(i + 1, 0, moved);
      updateSelected({ ...selected, actions: updated });
    }
  };

  return (
    <div className="App">
      <h4>Field action config JSON builder</h4>

      <Input.TextArea
        rows={10}
        placeholder="Paste JSON here..."
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        style={{ resize: "none", width: "100%" }}
      />

      <div style={{ marginTop: 20, marginBottom: 8 }}>
        <Flex wrap gap="small">
          <Button
            onClick={loadJson}
            color="primary"
            variant="solid"
            icon={<ReloadOutlined />}
          >
            Load JSON
          </Button>

          <Button
            onClick={() => {
              try {
                JSON.parse(jsonInput);
                navigator.clipboard.writeText(jsonInput);
                message.success("JSON copied!");
              } catch {
                message.error("Invalid JSON format");
              }
            }}
            color="pink"
            variant="solid"
            icon={<CopyOutlined />}
          >
            Copy JSON
          </Button>

          <Button
            onClick={() => setJsonInput(beautifyJson(jsonInput))}
            color="purple"
            variant="solid"
            icon={<ExportOutlined />}
          >
            Beautify
          </Button>

          <Button
            onClick={() => setJsonInput(minifyJson(jsonInput))}
            color="orange"
            variant="solid"
            icon={<MinusCircleOutlined />}
          >
            Minify
          </Button>

          <Button
            onClick={() => setJsonInput(validateJson(jsonInput))}
            color="danger"
            variant="solid"
            icon={<CheckCircleOutlined />}
          >
            Validate
          </Button>

          <Button
            onClick={updateQueryJsonInput}
            color="cyan"
            variant="solid"
            icon={<CheckOutlined />}
          >
            Copy JSON Query
          </Button>
        </Flex>
      </div>

      <h3>Buttons</h3>

      {buttons.map((btn) => (
        <div
          key={btn.id}
          style={{ marginBottom: 8, padding: 6, border: "1px solid #ccc" }}
        >
          <Flex gap="small">
            <b>{btn.label || "Untitled"}</b>
            <Button
              onClick={() => setSelectedId(btn.id)}
              icon={<EditOutlined />}
              color="primary"
              variant="dashed"
              size="small"
            />
            <Button
              onClick={() => deleteButton(btn.id)}
              icon={<DeleteOutlined />}
              color="danger"
              variant="dashed"
              size="small"
            />
            <Button
              onClick={() => moveButtonUp(btn.id)}
              icon={<UpOutlined />}
              color="geekblue"
              variant="dashed"
              size="small"
            />
            <Button
              onClick={() => moveButtonDown(btn.id)}
              icon={<DownOutlined />}
              color="magenta"
              variant="dashed"
              size="small"
            />
          </Flex>
        </div>
      ))}

      <Button onClick={addButton} type="primary" icon={<PlusCircleOutlined />}>
        Add Button
      </Button>

      {selected && (
        <div style={{ marginTop: 20 }}>
          <h3>Edit Button</h3>
          <table className="table table-bordered">
            <thead style={{ display: "none" }}>
              <tr>
                <th scope="col"></th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Label Name</td>
                <td>
                  <Input
                    value={selected.label}
                    onChange={(e) =>
                      updateSelected({ ...selected, label: e.target.value })
                    }
                    placeholder="Button label to be visible on screen"
                  />
                </td>
              </tr>
              <tr>
                <td>Icon Name</td>
                <td>
                  <Input
                    value={selected.icon}
                    onChange={(e) =>
                      updateSelected({ ...selected, icon: e.target.value })
                    }
                    placeholder="Eg - EditOutlined/HistoryOutlined"
                  />
                  <span>
                    <strong>Refer:</strong>{" "}
                    <a
                      href="https://ant.design/components/icon"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      List of icons
                    </a>
                  </span>
                </td>
              </tr>
              <tr>
                <td>Class Name</td>
                <td>
                  <Input
                    value={selected.className}
                    onChange={(e) =>
                      updateSelected({ ...selected, className: e.target.value })
                    }
                    placeholder="Button class name eg:btn-submit"
                  />
                </td>
              </tr>
              <tr>
                <td>Action Type</td>
                <td>
                  <Input
                    value={selected.actionType}
                    onChange={(e) =>
                      updateSelected({
                        ...selected,
                        actionType: e.target.value,
                      })
                    }
                    placeholder="System defined Action Type : DynamicFormActionSubmit"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <h4>Actions</h4>
          {selected.actions.map((act, i) => (
            <div key={crypto.randomUUID()}  style={{ border: "1px dashed gray", padding: 5, marginBottom: 5 }} >
              <Flex gap="small">
                <Button
                  onClick={() => moveActionUp(i)}
                  icon={<UpOutlined />}
                  color="geekblue"
                  variant="dashed"
                  size="small"
                />
                <Button
                  onClick={() => moveActionDown(i)}
                  icon={<DownOutlined />}
                  color="magenta"
                  variant="dashed"
                  size="small"
                />
              </Flex>
              <label htmlFor="ActionId">Action ID:</label>
              <Input
                id="ActionId"
                value={act.actionID}
                onChange={(e) => {
                  const updated = selected.actions.map((a, idx) =>
                    idx === i ? { ...a, actionID: e.target.value } : a
                  );
                  updateSelected({ ...selected, actions: updated });
                }}
                placeholder="Should be unique for each action"
              />
              <label>
                Exec Type:{" "}
                <span>(Select whether to execute SP/Query or Function)</span>
              </label>
              <Select
                value={act.execType}
                onChange={(val) => handleExecTypeChange(val, i)}
                style={{ width: "100%" }}
              >
                <Option value="">Select</Option>
                <Option value="SP">SP</Option>
                <Option value="Query">Query</Option>
                <Option value="Function">Function</Option>
              </Select>
              <label htmlFor="ExecData">Exec Data:</label>
              <Input
                id="ExecData"
                value={act.execData}
                onChange={(e) => {
                  const updated = selected.actions.map((a, idx) =>
                    idx === i ? { ...a, execData: e.target.value } : a
                  );
                  updateSelected({ ...selected, actions: updated });
                }}
                placeholder="Mention either function name, stored procedure name or query basis the execution type"
              />
            </div>
          ))}
          <Button
            onClick={() =>
              updateSelected({
                ...selected,
                actions: [
                  ...selected.actions,
                  { actionID: "", execType: "", execData: "" },
                ],
              })
            }
            icon={<PlusCircleOutlined />}
            color="cyan"
            variant="solid"
          >
            Add Action
          </Button>
        </div>
      )}
    </div>
  );
};

export default FieldActionConfigBuilder;
